import React, { useState, useEffect } from "react";
import { Mail, Lock, ArrowLeft, Sun, Moon, Loader2, Eye, EyeOff, ShieldCheck, ChevronRight } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

export default function CompanyLogin() {
  const [email, setEmail]       = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw]     = useState(false);
  const [apiError, setApiError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [theme, setTheme]       = useState(
    () => typeof window !== "undefined" ? localStorage.getItem("theme") || "light" : "light"
  );
  const navigate = useNavigate();
  const D = theme === "dark";

  useEffect(() => {
    const root = window.document.documentElement;
    D ? root.classList.add("dark") : root.classList.remove("dark");
    localStorage.setItem("theme", theme);
  }, [theme, D]);

  useEffect(() => {
    if (localStorage.getItem("user")) navigate("/company-dashboard", { replace: true });
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setApiError("");
    if (!email || !password) { setApiError("Please enter your email and password."); return; }
    setIsLoading(true);
    try {
      const res  = await fetch(`${process.env.REACT_APP_API_URL || "http://localhost:8000"}/api/company/login/`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (res.ok) {
        if (data.token) localStorage.setItem("user_token", data.token);
        localStorage.setItem("user", JSON.stringify(data.user || { email }));
        navigate("/company-dashboard", { replace: true });
      } else {
        setApiError(data.message || "Incorrect email or password. Please try again.");
      }
    } catch {
      setApiError("Connection error — check your internet and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // ── Tokens — site navy palette, NOT slate-950 ─────────────────────────────
  const bg       = D ? "#0d1f3c"                : "#f8fafc";
  const panelBg  = D ? "#142a4d"                : "#ffffff";
  const border   = D ? "rgba(255,255,255,0.10)" : "#e2e8f0";
  const text     = D ? "#f0f4f8"                : "#0f172a";
  const textMid  = D ? "rgba(240,244,248,0.75)" : "#475569";
  const textSoft = D ? "rgba(240,244,248,0.50)" : "#94a3b8";
  const inputBg  = D ? "rgba(255,255,255,0.06)" : "#f8fafc";
  const amber    = "#f59e0b";
  const amberL   = "#fbbf24";
  const sideBg   = "linear-gradient(160deg,#0d1f3c 0%,#1a3358 55%,#0f2d56 100%)";

  const inp = {
    width:"100%", padding:"11px 11px 11px 40px", borderRadius:11,
    border:`1.5px solid ${border}`, background:inputBg, color:text,
    fontSize:14, outline:"none", fontFamily:"inherit",
    transition:"border-color .15s", boxSizing:"border-box",
  };

  return (
    <div style={{ minHeight:"100vh", display:"flex", fontFamily:"'Inter',sans-serif", background:bg, transition:"background .3s" }}>

      {/* ── Left brand panel (desktop only) ── */}
      <div style={{ flex:"0 0 400px", maxWidth:400, background:sideBg, flexDirection:"column", padding:"48px 40px", position:"relative", overflow:"hidden" }}
        className="hidden lg:flex">
        {/* dot-grid texture */}
        <div aria-hidden style={{ position:"absolute", inset:0, backgroundImage:"radial-gradient(rgba(255,255,255,0.07) 1px,transparent 1px)", backgroundSize:"24px 24px", pointerEvents:"none" }} />
        <div aria-hidden style={{ position:"absolute", top:"-10%", left:"-10%", width:300, height:300, borderRadius:"50%", background:"radial-gradient(circle,rgba(251,191,36,0.18) 0%,transparent 70%)", pointerEvents:"none" }} />
        <div aria-hidden style={{ position:"absolute", bottom:"-8%", right:"-15%", width:340, height:340, borderRadius:"50%", background:"radial-gradient(circle,rgba(59,130,246,0.15) 0%,transparent 70%)", pointerEvents:"none" }} />

        {/* Logo */}
        <Link to="/" style={{ textDecoration:"none", display:"inline-flex", alignItems:"center", gap:10, marginBottom:"auto" }}>
          <div style={{ width:40, height:40, borderRadius:10, background:amber, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:900, fontSize:19, color:"#0d1f3c", flexShrink:0 }}>P</div>
          <span style={{ fontSize:19, fontWeight:800, color:"#fff" }}>Pilot<span style={{ color:amberL }}>Link</span> <span style={{ fontSize:11, fontWeight:600, color:"rgba(255,255,255,0.45)" }}>USA</span></span>
        </Link>

        <div style={{ position:"relative", zIndex:1, marginTop:52, marginBottom:44 }}>
          <div style={{ fontSize:11, fontWeight:700, letterSpacing:".14em", color:amberL, textTransform:"uppercase", marginBottom:14 }}>Company Portal</div>
          <h1 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:"clamp(26px,3vw,36px)", fontWeight:900, color:"#fff", lineHeight:1.15, marginBottom:18 }}>
            The Platform<br/>Built for<br/><span style={{ color:amberL }}>Pilot Car Pros.</span>
          </h1>
          <p style={{ fontSize:14, color:"rgba(255,255,255,0.60)", lineHeight:1.8 }}>
            Post loads, search verified drivers, verify certifications, and receive invoices — all in one place.
          </p>
        </div>

        <div style={{ position:"relative", zIndex:1, display:"flex", flexDirection:"column", gap:11, marginBottom:40 }}>
          {[
            ["📋","Post unlimited oversize loads"],
            ["🔍","Search verified, certified drivers"],
            ["✅","1-click cert verification via API"],
            ["📄","Receive & approve driver invoices"],
          ].map(([icon, text]) => (
            <div key={text} style={{ display:"flex", alignItems:"center", gap:12 }}>
              <div style={{ width:32, height:32, borderRadius:8, background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.12)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, flexShrink:0 }}>{icon}</div>
              <span style={{ fontSize:13, color:"rgba(255,255,255,0.75)", fontWeight:500 }}>{text}</span>
            </div>
          ))}
        </div>

        <div style={{ position:"relative", zIndex:1, display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
          {[["50","States Active"],["Free","First Post"],["1-Click","Cert Verify"],["$0","Setup Fee"]].map(([n,l]) => (
            <div key={l} style={{ padding:"12px 14px", borderRadius:11, background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.10)" }}>
              <div style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:18, fontWeight:800, color:amberL }}>{n}</div>
              <div style={{ fontSize:10, color:"rgba(255,255,255,0.48)", marginTop:2 }}>{l}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Right form panel ── */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:"100vh" }}>
        {/* Top bar */}
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"20px 24px 0" }}>
          <Link to="/" style={{ display:"flex", alignItems:"center", gap:7, textDecoration:"none", color:textMid, fontSize:13, fontWeight:600 }}>
            <ArrowLeft size={15}/><span className="hidden sm:inline">Back to Home</span>
          </Link>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <span style={{ fontSize:12, color:textSoft }}>No account?</span>
            <Link to="/signup" style={{ fontSize:13, fontWeight:700, color:D?amberL:amber, textDecoration:"none", padding:"7px 16px", borderRadius:99, border:`1.5px solid ${D?amberL:amber}` }}>
              Join Free →
            </Link>
            <button onClick={() => setTheme(D?"light":"dark")} aria-label="Toggle theme"
              style={{ width:36, height:36, borderRadius:9, border:`1px solid ${border}`, background:inputBg, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", color:textMid }}>
              {D ? <Sun size={15}/> : <Moon size={15}/>}
            </button>
          </div>
        </div>

        {/* Form */}
        <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center", padding:"32px 24px" }}>
          <div style={{ width:"100%", maxWidth:400 }}>

            {/* Mobile logo */}
            <div className="lg:hidden" style={{ display:"flex", alignItems:"center", gap:9, marginBottom:28 }}>
              <div style={{ width:34, height:34, borderRadius:8, background:amber, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:900, fontSize:15, color:"#0d1f3c" }}>P</div>
              <span style={{ fontSize:16, fontWeight:800, color:text }}>Pilot<span style={{ color:amber }}>Link</span> <span style={{ fontSize:10, color:textSoft, fontWeight:500 }}>USA</span></span>
            </div>

            <div style={{ marginBottom:28 }}>
              <h2 style={{ fontFamily:"'Plus Jakarta Sans',sans-serif", fontSize:26, fontWeight:900, color:text, marginBottom:6, lineHeight:1.2 }}>🏢 Company Sign In</h2>
              <p style={{ fontSize:14, color:textMid }}>Access your PilotLink company account.</p>
            </div>

            {/* Card */}
            <div style={{ background:panelBg, border:`1px solid ${border}`, borderRadius:18, padding:"28px 26px", boxShadow:D?"0 4px 32px rgba(0,0,0,0.25)":"0 4px 24px rgba(13,31,60,0.08)" }}>

              {apiError && (
                <div style={{ marginBottom:16, padding:"11px 14px", borderRadius:10, background:D?"rgba(251,113,133,0.10)":"#fff1f2", border:`1px solid ${D?"rgba(251,113,133,0.25)":"#fecdd3"}`, fontSize:13, color:D?"#fda4af":"#be123c", fontWeight:500 }}>
                  ⚠ {apiError}
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                {/* Email */}
                <div style={{ marginBottom:16 }}>
                  <label style={{ display:"block", fontSize:12, fontWeight:700, color:textMid, marginBottom:7, letterSpacing:".03em" }}>Email Address</label>
                  <div style={{ position:"relative" }}>
                    <Mail size={15} style={{ position:"absolute", left:13, top:"50%", transform:"translateY(-50%)", color:textSoft, pointerEvents:"none" }}/>
                    <input type="email" value={email} placeholder="admin@company.com" autoComplete="email" required
                      onChange={e => { setEmail(e.target.value); setApiError(""); }}
                      onFocus={e => e.target.style.borderColor=D?"rgba(251,191,36,0.60)":"#f59e0b"}
                      onBlur={e  => e.target.style.borderColor=border}
                      style={inp}/>
                  </div>
                </div>

                {/* Password */}
                <div style={{ marginBottom:22 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:7 }}>
                    <label style={{ fontSize:12, fontWeight:700, color:textMid, letterSpacing:".03em" }}>Password</label>
                    <Link to="/forgot-password" style={{ fontSize:12, fontWeight:600, color:D?amberL:amber, textDecoration:"none" }}>Forgot password?</Link>
                  </div>
                  <div style={{ position:"relative" }}>
                    <Lock size={15} style={{ position:"absolute", left:13, top:"50%", transform:"translateY(-50%)", color:textSoft, pointerEvents:"none" }}/>
                    <input type={showPw?"text":"password"} value={password} placeholder="••••••••" autoComplete="current-password" required
                      onChange={e => { setPassword(e.target.value); setApiError(""); }}
                      onFocus={e => e.target.style.borderColor=D?"rgba(251,191,36,0.60)":"#f59e0b"}
                      onBlur={e  => e.target.style.borderColor=border}
                      style={{ ...inp, paddingRight:44 }}/>
                    <button type="button" onClick={() => setShowPw(p=>!p)}
                      style={{ position:"absolute", right:12, top:"50%", transform:"translateY(-50%)", background:"none", border:"none", cursor:"pointer", color:textSoft, display:"flex" }}>
                      {showPw ? <EyeOff size={15}/> : <Eye size={15}/>}
                    </button>
                  </div>
                </div>

                {/* Submit — amber/gold matching site */}
                <button type="submit" disabled={isLoading} style={{
                  width:"100%", padding:"13px", borderRadius:11, border:"none",
                  cursor:isLoading?"not-allowed":"pointer",
                  background:isLoading?"rgba(245,158,11,0.55)":`linear-gradient(135deg,${amber},${amberL})`,
                  color:"#0d1f3c", fontSize:15, fontWeight:800,
                  display:"flex", alignItems:"center", justifyContent:"center", gap:8,
                  boxShadow:isLoading?"none":"0 4px 16px rgba(245,158,11,0.38)",
                  transition:"all .15s", fontFamily:"inherit",
                }}
                onMouseEnter={e=>{ if(!isLoading) e.currentTarget.style.filter="brightness(1.06)"; }}
                onMouseLeave={e=>{ e.currentTarget.style.filter="none"; }}>
                  {isLoading
                    ? <><Loader2 size={17} style={{ animation:"spin 1s linear infinite" }}/> Signing you in…</>
                    : <>Sign In <ChevronRight size={17}/></>}
                </button>
              </form>

              {/* Footer links */}
              <div style={{ marginTop:20, paddingTop:18, borderTop:`1px solid ${border}`, display:"flex", flexDirection:"column", alignItems:"center", gap:10 }}>
                <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:textSoft }}>
                  <ShieldCheck size={13} style={{ color:"#22c55e" }}/>
                  Secured with 256-bit TLS encryption
                </div>
                <p style={{ fontSize:13, color:textMid, margin:0 }}>
                  New to PilotLink?{" "}
                  <Link to="/signup" style={{ fontWeight:800, color:D?amberL:amber, textDecoration:"none" }}>Create a free account →</Link>
                </p>
              </div>
            </div>

            {/* Other login options */}
          </div>
        </div>

        <div style={{ padding:"14px 24px", textAlign:"center", fontSize:12, color:textSoft, borderTop:`1px solid ${border}` }}>
          © {new Date().getFullYear()} PilotLink USA. All rights reserved.
        </div>
      </div>

      <style>{`@keyframes spin{to{transform:rotate(360deg);}}`}</style>
    </div>
  );
}
